import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Sparkles, CheckCircle2, XCircle, RefreshCw, FileText, Eye, ChevronDown, ChevronUp,
  Loader2, ListChecks,
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OBJECT_TYPE_META = {
  decision_tree: { label: 'Decision Tree', color: '#5856D6' },
  faq: { label: 'FAQ', color: '#007AFF' },
  checklist: { label: 'Checklist', color: '#34C759' },
  pattern_card: { label: 'Pattern Card', color: '#FF9500' },
  anti_pattern: { label: 'Anti-pattern', color: '#FF3B30' },
  runbook: { label: 'Runbook', color: '#8B5CF6' },
};

export default function SynthesisDraftsCard({ headers }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [rejectDraftId, setRejectDraftId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busyDraftId, setBusyDraftId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/cop/synthesis/drafts?status=draft`, { headers });
      setDrafts(r.data.drafts || []);
    } catch {
      // Non-fatal; non-staff users get 403 — don't toast noise
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const onDrafted = () => load();
    window.addEventListener('cop:synthesis-drafted', onDrafted);
    return () => window.removeEventListener('cop:synthesis-drafted', onDrafted);
    /* eslint-disable-next-line */
  }, []);

  const approve = async (draft) => {
    setBusyDraftId(draft.draft_id);
    try {
      const r = await axios.post(`${API}/cop/synthesis/drafts/${draft.draft_id}/approve`, {}, { headers });
      const individuals = r.data.ontology_individuals || [];
      const firstIndId = individuals[0]?.individual_id;
      const indCount = individuals.length;
      const msg = indCount > 0
        ? `${r.data.object_type} published · ${indCount} ontology individual${indCount === 1 ? '' : 's'} created`
        : `${r.data.object_type} published — note + article created`;
      toast.success(msg, {
        duration: 6000,
        action: firstIndId
          ? {
              label: 'View in graph',
              onClick: () => window.open(`/ontology?focus=${firstIndId}`, '_blank'),
            }
          : { label: 'View article', onClick: () => window.open(`/articles`, '_blank') },
      });
      setDrafts(prev => prev.filter(d => d.draft_id !== draft.draft_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    } finally { setBusyDraftId(null); }
  };

  const openReject = (draft) => {
    setRejectDraftId(draft.draft_id);
    setRejectReason('');
  };

  const submitReject = async () => {
    if (!rejectDraftId) return;
    setBusyDraftId(rejectDraftId);
    try {
      await axios.post(
        `${API}/cop/synthesis/drafts/${rejectDraftId}/reject`,
        { reason: rejectReason.trim() },
        { headers },
      );
      toast.success('Draft rejected — signal stays resonant for re-synthesis');
      setDrafts(prev => prev.filter(d => d.draft_id !== rejectDraftId));
      setRejectDraftId(null);
      setRejectReason('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reject failed');
    } finally { setBusyDraftId(null); }
  };

  // If non-staff or no drafts + not loading, hide entirely to keep the page clean
  if (!loading && drafts.length === 0) return null;

  return (
    <>
      <Card className="border-[#5856D6]/30 bg-[#5856D6]/3" data-testid="synthesis-drafts-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#5856D6]" />
              Stage 3 · Synthesis drafts awaiting review
              <Badge className="text-[9px] bg-[#5856D6]/10 text-[#5856D6] border-0">
                {drafts.length} pending
              </Badge>
            </CardTitle>
            <Button
              size="sm" variant="ghost" onClick={load} disabled={loading}
              data-testid="synthesis-refresh"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading && drafts.length === 0 ? (
            <div className="text-center py-6">
              <Loader2 className="w-5 h-5 animate-spin mx-auto text-[#9CA3AF]" />
            </div>
          ) : (
            <div className="space-y-2">
              {drafts.map(draft => {
                const meta = OBJECT_TYPE_META[draft.object_type] || OBJECT_TYPE_META.runbook;
                const expanded = expandedId === draft.draft_id;
                const conf = Math.round((draft.confidence || 0) * 100);
                return (
                  <div
                    key={draft.draft_id}
                    className="bg-white border border-[#E5E7EB] rounded-lg p-3"
                    data-testid={`draft-${draft.draft_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: `${meta.color}15` }}
                      >
                        <FileText className="w-3.5 h-3.5" style={{ color: meta.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <h4 className="text-sm font-semibold text-[#0A0A0A]">{draft.title}</h4>
                          <Badge
                            className="text-[9px] border-0 flex-shrink-0"
                            style={{ backgroundColor: `${meta.color}15`, color: meta.color }}
                          >
                            {meta.label}
                          </Badge>
                        </div>
                        <p className="text-[11px] text-[#4B5563] mt-1 line-clamp-2">
                          {draft.article_summary}
                        </p>
                        <div className="flex items-center gap-3 mt-1.5 flex-wrap text-[10px] text-[#9CA3AF]">
                          <span>Signal: {draft.signal_creator_name}</span>
                          <span>·</span>
                          <span>Drafted {format(new Date(draft.created_at), 'MMM d, HH:mm')}</span>
                          <span>·</span>
                          <span className="inline-flex items-center gap-1">
                            Confidence
                            <span
                              className="inline-block w-12 h-1 bg-[#F3F4F6] rounded-full overflow-hidden align-middle"
                            >
                              <span
                                className="block h-full"
                                style={{ width: `${conf}%`, backgroundColor: meta.color }}
                              />
                            </span>
                            <strong className="text-[#4B5563]">{conf}%</strong>
                          </span>
                        </div>
                        {expanded && (
                          <div
                            className="mt-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-md p-3 max-h-[320px] overflow-y-auto"
                            data-testid={`draft-preview-${draft.draft_id}`}
                          >
                            <pre className="whitespace-pre-wrap text-[11px] text-[#0A0A0A] font-mono leading-relaxed">
                              {draft.content_md}
                            </pre>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setExpandedId(expanded ? null : draft.draft_id)}
                            className="h-7 text-[11px] text-[#4B5563]"
                            data-testid={`draft-toggle-${draft.draft_id}`}
                          >
                            {expanded ? <ChevronUp className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                            {expanded ? 'Hide preview' : 'Preview'}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => approve(draft)}
                            disabled={busyDraftId === draft.draft_id}
                            className="h-7 text-[11px] bg-[#34C759] hover:bg-[#2BA44A] text-white"
                            data-testid={`draft-approve-${draft.draft_id}`}
                          >
                            {busyDraftId === draft.draft_id
                              ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              : <CheckCircle2 className="w-3 h-3 mr-1" />}
                            Approve & publish
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openReject(draft)}
                            disabled={busyDraftId === draft.draft_id}
                            className="h-7 text-[11px] border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/5"
                            data-testid={`draft-reject-${draft.draft_id}`}
                          >
                            <XCircle className="w-3 h-3 mr-1" />Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reject-with-reason dialog */}
      <Dialog open={!!rejectDraftId} onOpenChange={(o) => !o && setRejectDraftId(null)}>
        <DialogContent className="max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks className="w-4 h-4 text-[#FF3B30]" />Reject synthesis draft
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#4B5563]">
              The signal will remain resonant and can be re-synthesised on the next pass (or via manual trigger).
            </p>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-medium">
                Reason (optional)
              </label>
              <Textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="Object type mismatch · missing context · wrong framing…"
                rows={3}
                maxLength={500}
                className="mt-1 text-xs"
                data-testid="reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectDraftId(null)}
              data-testid="reject-cancel"
            >Cancel</Button>
            <Button
              onClick={submitReject}
              disabled={!!busyDraftId}
              className="bg-[#FF3B30] hover:bg-[#D32F2F] text-white"
              data-testid="reject-confirm"
            >
              {busyDraftId ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}
              Reject draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
